/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { usePlayer } from "@packages/media";
import type { QueueContentType, ReferenceType } from "@packages/sdk";
import type { RGBAObject, ThemeType, ThumbnailProps } from "@packages/ui";
import {
  cssMediaQueryMinWidthTablet,
  numericPercentages,
  numericPixels,
  spacing,
  Thumbnail,
  useHover,
  usePress,
} from "@packages/ui";
import type { ComponentProps, MouseEventHandler, ReactNode } from "react";
import { forwardRef, useCallback } from "react";

import type { MetaData } from "../../lib";
import { ContentItemContent as Content } from "../ContentItemContent";

const containerStyles: Interpolation = {
  alignItems: "center",
  cursor: "pointer",
  display: "flex",
  gap: spacing.ms,
  maxWidth: numericPercentages[100],
  scrollSnapAlign: "center",
  transition: "opacity 150ms ease-in, scale 150ms ease-in",
  width: "fit-content",
};

const hoveredContainerStyles: Interpolation = {
  scale: 1.02,
};

const pressedContainerStyles: Interpolation = {
  opacity: 0.6,
  scale: 0.975,
};

const rightActionContainerStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  alignItems: "center",
  color: theme.semanticColors.primary,
  display: "flex",
  flexShrink: 0,
  justifyContent: "center",
  paddingRight: spacing.m,
});

const disabledContainerStyles: Interpolation = {
  cursor: "default",
};

const imageHoverStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  transition: "outline 150ms ease-in",
  outline: `${numericPixels[2]} solid ${theme.semanticColors.neutralsLowest}`,
  [cssMediaQueryMinWidthTablet]: {
    outline: `${numericPixels[4]} solid ${theme.semanticColors.neutralsLowest}`,
  },
});

const sizeContainerStyles: Record<string, Interpolation<ThemeType>> = {
  xxs: {
    maxWidth: spacing.full,
    width: spacing.full,
  },
  xs: {
    maxWidth: spacing.full,
    width: spacing.full,
  },
  s: {
    maxWidth: spacing.full,
    width: spacing.full,
  },
  m: {
    maxWidth: 160,
    // 124px minimum supports screens down to 300px width
    // 168px minimum supports screens 768px width and up
    minWidth: 124,
    width: spacing.full,
    [cssMediaQueryMinWidthTablet]: { maxWidth: 210, minWidth: 168 },
  },
  l: {
    minWidth: 327,
    width: spacing.full,
    [cssMediaQueryMinWidthTablet]: { minWidth: spacing.full },
  },
};

export type ContentItemProps = ComponentProps<"div"> & {
  description?: string;
  metaData?: MetaData;
  rightAction?: ReactNode;
  backgroundColor?: RGBAObject;
  childProps?: Omit<ComponentProps<"div">, "onPlay">;
  disabled?: boolean;
  referenceType?: ReferenceType;
  referenceId?: number;
  disableAnimationsOnly?: boolean;
  size: "xxs" | "xs" | "s";
  css?: Interpolation<ThemeType>;
} & Pick<ThumbnailProps, "title" | "imageSrc" | "imageOverlay">;

const DEFAULT_LARGE_DESKTOP_CONTENT_ITEM_BACKGROUND = {
  r: 0,
  g: 0,
  b: 0,
  a: 255,
};

export const ContentItem = forwardRef<HTMLDivElement, ContentItemProps>(
  (
    {
      css: cssProp,
      size,
      imageSrc,
      imageOverlay,
      title,
      description,
      metaData = {},
      rightAction,
      childProps,
      disabled = false,
      disableAnimationsOnly = false,
      backgroundColor = DEFAULT_LARGE_DESKTOP_CONTENT_ITEM_BACKGROUND,
      referenceType,
      referenceId,
      ...props
    },
    ref,
  ) => {
    const player = usePlayer();
    const { hoverProps, isHovered } = useHover({
      isDisabled: disabled || disableAnimationsOnly,
    });
    const { pressProps, isPressed } = usePress({ isDisabled: disabled });

    const onPlay: MouseEventHandler<HTMLButtonElement> = useCallback(
      async (e) => {
        if (!referenceType || !referenceId) return;

        if (
          !["chapter", "challenge", "collection", "prayer", "audio"].includes(
            referenceType,
          )
        )
          return;
        e.stopPropagation();
        await player?.playNew({
          type: referenceType as QueueContentType,
          id: referenceId,
        });
      },
      [player, referenceId, referenceType],
    );

    return (
      <div
        ref={ref}
        {...hoverProps}
        {...pressProps}
        css={[
          containerStyles,
          sizeContainerStyles[size],
          isHovered && !disableAnimationsOnly && hoveredContainerStyles,
          isPressed && !disableAnimationsOnly && pressedContainerStyles,
          disabled && disabledContainerStyles,
          cssProp,
        ]}
        {...props}
      >
        <Thumbnail
          imageSrc={imageSrc}
          imageOverlay={imageOverlay}
          size={size}
          title={title}
          css={isHovered ? [imageHoverStyles] : []}
          {...childProps}
        />
        <Content
          title={title}
          description={description}
          metaData={metaData}
          size={size}
          onPlay={onPlay}
          {...childProps}
        />
        {["xs", "s"].includes(size) && rightAction && (
          <div css={rightActionContainerStyles}>{rightAction}</div>
        )}
      </div>
    );
  },
);

ContentItem.displayName = "ContentItem";
